import React, { useState } from "react";
import { THEME, flexxState, awardXP } from "./state";
import Portal from "./Portal";

// 3-step onboarding modal: welcome → personalize → expectations
export default function OnboardingFlow({ defaultName, onFinish }) {
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState({
    name: defaultName || "",
    market: "Forex",
    experience: "Complete Beginner",
    studyTime: "30 min",
  });

  const next = () => setStep((s) => s + 1);

  const finish = () => {
    flexxState.saveProfile(profile);
    flexxState.setOnboarded(true);
    awardXP(50, "Onboarding complete");
    onFinish && onFinish(profile);
  };

  return (
    <Portal>
    <div data-testid="flexx-onboarding-modal" style={overlay}>
      <div className="flexx-slide-up" style={container}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              style={{
                flex: 1, height: 4, borderRadius: 999,
                background: s <= step ? `linear-gradient(90deg, ${THEME.teal}, ${THEME.gold})` : THEME.border,
              }}
            />
          ))}
        </div>

        {step === 1 && (
          <div className="flexx-fade-in">
            <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 6 }}>STEP 1 OF 3</div>
            <h2 data-testid="onboarding-welcome-title" style={{ color: THEME.text, fontSize: 24, margin: "0 0 12px" }}>Welcome to FLEXX Academy</h2>
            <p style={{ color: THEME.mute, lineHeight: 1.65, fontSize: 14, margin: 0 }}>
              You're about to learn trading the right way — with AI-powered lessons that adapt to <b style={{ color: THEME.text }}>your</b> level.
              No fluff. No guesswork. Just a proven system.
            </p>
            <button data-testid="onboarding-step1-cta" className="flexx-btn-primary" style={{ width: "100%", marginTop: 22 }} onClick={next}>
              Let's Get Started →
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="flexx-fade-in">
            <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 6 }}>STEP 2 OF 3</div>
            <h2 style={{ color: THEME.text, fontSize: 22, margin: "0 0 6px" }}>Tell us about yourself</h2>
            <p style={{ color: THEME.mute, fontSize: 13, marginBottom: 18 }}>We'll personalize chart examples and quiz scenarios to your market.</p>

            <label style={labelStyle}>Your name</label>
            <input
              data-testid="onboarding-name-input"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              style={inputStyle}
              placeholder="e.g. Alex"
            />

            <label style={labelStyle}>Primary market</label>
            <ChipGroup testPrefix="market" value={profile.market} options={["Forex", "Crypto", "Stocks", "Indices"]} onChange={(v) => setProfile({ ...profile, market: v })} />

            <label style={{ ...labelStyle, marginTop: 14 }}>Experience</label>
            <ChipGroup testPrefix="experience" value={profile.experience} options={["Complete Beginner", "Some Knowledge", "Active Trader"]} onChange={(v) => setProfile({ ...profile, experience: v })} />

            <label style={{ ...labelStyle, marginTop: 14 }}>Daily study time</label>
            <ChipGroup testPrefix="study" value={profile.studyTime} options={["15 min", "30 min", "1 hour", "2+ hours"]} onChange={(v) => setProfile({ ...profile, studyTime: v })} />

            <button
              data-testid="onboarding-step2-cta"
              className="flexx-btn-primary"
              style={{ width: "100%", marginTop: 22 }}
              disabled={!profile.name.trim()}
              onClick={next}
            >
              Continue →
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="flexx-fade-in">
            <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 6 }}>STEP 3 OF 3</div>
            <h2 style={{ color: THEME.text, fontSize: 22, margin: "0 0 10px" }}>How FLEXX works</h2>
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 18px", display: "grid", gap: 10 }}>
              {[
                "You learn. You prove it. Then you advance.",
                "Your AI tutor adapts to your pace.",
                "You review what you learn so it sticks.",
                "You complete a real trading plan to graduate.",
              ].map((t, i) => (
                <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, color: THEME.text, fontSize: 14, lineHeight: 1.55 }}>
                  <span style={{ color: THEME.green, marginTop: 2 }}>✓</span>
                  {t}
                </li>
              ))}
            </ul>
            <p style={{ color: THEME.mute, fontSize: 12, lineHeight: 1.6, marginTop: 6 }}>
              This is not a passive video course. This is a trading school.
            </p>
            <button data-testid="onboarding-start-diagnostic-btn" className="flexx-btn-primary" style={{ width: "100%", marginTop: 18 }} onClick={finish}>
              Start My Diagnostic →
            </button>
          </div>
        )}
      </div>
    </div>
    </Portal>
  );
}

function ChipGroup({ value, options, onChange, testPrefix }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            data-testid={`onboarding-${testPrefix}-${opt.toLowerCase().replace(/\s|\+/g, "-")}`}
            onClick={() => onChange(opt)}
            style={{
              padding: "8px 14px", borderRadius: 999, cursor: "pointer", fontSize: 13,
              background: active ? THEME.tealSoft : "transparent",
              border: `1px solid ${active ? THEME.teal : THEME.border}`,
              color: active ? THEME.text : THEME.mute,
              transition: "all 0.2s ease",
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

const overlay = {
  position: "fixed", inset: 0, zIndex: 9700,
  background: "rgba(0,0,0,0.9)",
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20,
};
const container = {
  background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 18, padding: 28,
  maxWidth: 460, width: "100%", maxHeight: "92vh", overflowY: "auto",
};
const labelStyle = { display: "block", color: THEME.mute, fontSize: 12, letterSpacing: 0.5, marginBottom: 8, textTransform: "uppercase" };
const inputStyle = {
  width: "100%", padding: "11px 14px", borderRadius: 10, marginBottom: 14,
  background: "#13161c", border: `1px solid ${THEME.border}`, color: THEME.text, fontSize: 14,
  outline: "none", boxSizing: "border-box",
};
